import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Routes, Route, useNavigate, useLocation, useNavigationType,
} from 'react-router-dom';
import { Navigation } from '@Components/Navigation';
import {
  LoginPage,
  DemoLandingPage,
  DemoSplashPage,
  ForgotPasswordPage,
  RecoverAccountPage,
  InvitedPage,
  SignupPage,
  CustomerOnboardingPage,
  AccountSetupPage,
  ProductNewSearchPage,
  ProductResultsUploadPage,
  ProductDashboardResultsPage,
  ProductReviewPage,
  ProductListSearchesPage,
  ProductPiracyOverviewPage,
  ProductReportPage,
  ProductReportsListPage,
  ProductLiveTrackerPage,
  ProductResultsInfringementsPage,
  ProductResultsRemovalsPage,
  ProductResultDetailsPage,
  NoAuthReportPage,
  ProductAssetsListPage,
  ProductAllowlistPage,
  ProductAssetDetailsPage,
  ProductAssetUploadPage,
  ProductAssetUploadDetailsPage,
  ProductClaimsListPage,
  ProductClaimDetailsPage,
  ManualClaimSubmitPage,
  ManualClaimRequestDetailPage,
  AccountPage,
  ManageSubscriptionPage,
  CidCancellationRequestPage,
  PlanUpgradePage,
  PayoutSetupPage,
  PayoutStatusPage,
  CompanySettingsPage,
  CompanyUsersPage,
  CreatorReferralsPage,
  AdminLoginPage,
  AdminAccountPage,
  AdminSearchesPage,
  AdminResultsPage,
  AdminCompliancePage,
  AdminNewFileImportPage,
  AdminImportsHistoryPage,
  AdminImportSearchesPage,
  AdminManualClaimsListPage,
  AdminManualClaimDetailPage,
  AdminMonetizationRequestsListPage,
  AdminMonetizationRequestDetailsPage,
  AdminPayoutDashboardPage,
  AdminCidPayoutStatusPage,
  AdminCustomOnboardingPage,
  AdminOnboardingsListPage,
  AdminOnboardingDetailsPage,
  AdminDemoLinksPage,
  AdminDemoSubmissionsPage,
  AdminCidAnalyticsDashboardPage,
  AdminYtAuditsPage,
  AdminNagraCandidateDetailPage,
  AdminNagraManageOperationsPage,
  AdminNagraManualSubmissionPage,
  AdminNagraReviewCandidatesPage,
  CyclopsAnalyticsPage,
  CyclopsEventsListPage,
  CyclopsEventDetailsPage,
  CyclopsSettingsPage,
  TeamsSettingsPage,
  CompanyUnitsPage,
  FingerprintingConfigPage,
  CyclopsOrganizationsPage,
  Error404Page,
} from '@Components/Page';
import { paths } from '@Components/configs';
import {
  clearRedirectRequest,
  initiateCompanyMaintenanceSettingsRequest,
  fetchCustomerOnboardingData,
} from '@Actions';
import Mixpanel from '@Helpers/mixpanel';
import companyFeatureFlags from '@Helpers/featureFlags';
import { setFaviconStatus, selectEnvironment } from '@Helpers/util';
import { AuthRouteWrapper, HomeRedirect } from './components/Layout';

Mixpanel.init();

const DEMO_OVERVIEW_CONFIG = {
  embedId: 'JNwng',
  darkModeEmbedId: '7pKOA',
  applyCompanyFilter: true,
};

const AppRoutes = ({
  size,
  isRedirectRequired,
  faviconStatus,
  clearRedirect,
  redirectPush,
  redirectPath = null,
  isAuthenticated,
  isDemo = false,
  isAdminAuthenticated,
  fetchOnboardingData,
  fetchCompanyMaintenanceSettings,
  companyMaintenanceSettings = null,
  companyId = null,
  companyName = null,
  companyOnboardingId = null,
  companyFeatures = null,
  email = null,
  firstName = null,
  lastName = null,
  paymentPending = false,
  paymentLink = null,
  stripeAccountSetupComplete = null,
  stripeAccountId = null,
  cyclopsConfig = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const navType = useNavigationType();

  React.useEffect(() => {
    if (!isRedirectRequired) {
      setFaviconStatus(faviconStatus);
    }

    if (isRedirectRequired && redirectPath) {
      navigate(redirectPath, { replace: redirectPush, state: { refresh: redirectPush } });
      window.scrollTo(0, 0);
      clearRedirect();
    }
  }, [isRedirectRequired, faviconStatus]);

  React.useEffect(() => {
    if (isAuthenticated) {
      Mixpanel.register({
        companyId,
        companyName,
        email,
        firstName,
        lastName,
      });

      if (!isDemo && !companyMaintenanceSettings) fetchCompanyMaintenanceSettings();
      if (!isDemo && companyOnboardingId) fetchOnboardingData(companyOnboardingId);
    }

    Mixpanel.track('page_view', {
      path: location.pathname,
      search: location.search,
      environment: selectEnvironment(),
    });
  }, [location, isAuthenticated]);

  const small = size.includes('small');
  const authProps = { location, navType };

  // Company feature flags
  const flags = {
    ...(companyFeatureFlags(
      companyFeatures, { stripeAccountSetupComplete, stripeAccountId },
    ) || {}),
    ...(isDemo ? { overviewEnabled: true } : {}),
  };

  // Cosmic configured feature flag checks
  const cyclopsDemoData = flags?.isCyclopsDemo && cyclopsConfig?.find(({ metadata }) => (
    metadata?.config?.companyId === companyId && metadata?.config?.companyName === companyName));

  // Maintenance settings derived flags
  const searchDisabled = Boolean(
    companyMaintenanceSettings?.disabledPages?.some((page) => page.disableSearchFeatures),
  );
  const reviewDisabled = Boolean(
    companyMaintenanceSettings?.disabledPages?.some((page) => page.disableReviewFeatures),
  );
  const resultsDisabled = Boolean(
    companyMaintenanceSettings?.disabledPages?.some((page) => page.disableResultsFeatures),
  );

  return (
    <Routes>
      {/* Home Page Redirect Handler */}
      <Route
        index
        path={paths.root}
        element={(
          <HomeRedirect
            isAuthenticated={isAuthenticated}
            isAdminAuthenticated={isAdminAuthenticated}
            flags={flags}
          />
        )}
      />
      {/* No Auth Routes (!isAdminAuthenticated && !isAuthenticated) */}
      <Route
        element={(
          <Navigation
            small={small}
            activePageType="noauth"
            companyMaintenanceSettings={companyMaintenanceSettings}
            {...authProps}
          />
        )}
      >
        <Route
          path={paths.login}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <LoginPage
                small={small}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminLogin}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminLoginPage
                small={small}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.forgotPassword}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <ForgotPasswordPage
                small={small}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.recoverAccount}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <RecoverAccountPage
                small={small}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.invited}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <InvitedPage
                small={small}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.signup}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <SignupPage
                small={small}
              />
            </AuthRouteWrapper>
          )}
        />
        {/* New generic customer onboarding flow */}
        <Route
          path={paths.customerOnboarding}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <CustomerOnboardingPage
                small={small}
                size={size}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.accountSetup}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AccountSetupPage
                small={small}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.sharedReport}
          element={(
            <AuthRouteWrapper
              type="noauth"
              isAuthenticated={isAuthenticated}
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <NoAuthReportPage
                small={small}
                location={location}
              />
            </AuthRouteWrapper>
          )}
        />
        {/* Demo — public routes, no auth required */}
        <Route
          path={paths.demoSplash}
          element={<DemoSplashPage />}
        />
        <Route
          path={paths.demoLanding}
          element={<DemoLandingPage small={small} />}
        />
        <Route
          path="*"
          element={(
            <Error404Page />
          )}
        />
      </Route>
      {/* Product Auth Routes (requires isAuthenticated & valid token) */}
      <Route
        element={(
          <Navigation
            small={small}
            activePageType="product"
            companyMaintenanceSettings={companyMaintenanceSettings}
            paymentPending={paymentPending}
            {...authProps}
          />
        )}
      >
        <Route
          path={paths.productNewSearch}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductNewSearchPage
                small={small}
                featureConfig={flags?.searchConfig}
                searchDisabled={searchDisabled}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productResultsUpload}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductResultsUploadPage
                small={small}
                featureConfig={flags?.searchConfig}
                resultsDisabled={flags?.resultsDisabled}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productSearchResults}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductDashboardResultsPage
                small={small}
                size={size}
                searchDisabled={searchDisabled}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productInfringements}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductResultsInfringementsPage
                small={small}
                size={size}
                resultsDisabled={resultsDisabled}
                paymentPending={paymentPending}
                featureConfig={flags?.settingsConfig}
                contentIdEnabled={flags?.contentIdEnabled}
                monitoringEnabled={Boolean(flags?.searchEnabled || flags?.reviewEnabled)}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productConfirmedRemovals}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductResultsRemovalsPage
                small={small}
                size={size}
                resultsDisabled={resultsDisabled}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productResultsDetails}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductResultDetailsPage
                small={small}
                resultsDisabled={resultsDisabled}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productReview}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductReviewPage
                small={small}
                size={size}
                reviewDisabled={reviewDisabled}
                paymentPending={paymentPending}
                contentIdEnabled={flags?.contentIdEnabled}
                monitoringEnabled={Boolean(flags?.searchEnabled || flags?.reviewEnabled)}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productListSearches}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductListSearchesPage
                small={small}
                size={size}
                searchFeatureConfig={flags?.searchConfig}
                reviewEnabled={flags?.reviewEnabled}
                searchDisabled={searchDisabled}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productPiracyOverview}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductPiracyOverviewPage
                small={small}
                size={size}
                embedConfig={isDemo ? DEMO_OVERVIEW_CONFIG : flags?.overviewConfig}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productReports}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductReportsListPage
                small={small}
                size={size}
                categoryReports={flags?.reportsConfig}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productReport}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductReportPage
                small={small}
                size={size}
                categoryReports={flags?.reportsConfig}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productLiveTracker}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductLiveTrackerPage
                small={small}
                size={size}
                paymentPending={paymentPending}
                featureConfig={flags?.settingsConfig}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productListAssets}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductAssetsListPage
                small={small}
                size={size}
                featureConfig={{
                  feature: flags?.contentIdConfig,
                  contentIdAssetUploadEnabled: flags?.contentIdAssetUploadEnabled,
                  hasAllowlistedChannels: flags?.hasAllowlistedChannels,
                }}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productAllClaimsList}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductClaimsListPage
                small={small}
                size={size}
                featureConfig={flags?.contentIdConfig}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productCidAllowlist}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductAllowlistPage
                small={small}
                size={size}
                featureConfig={flags?.contentIdConfig}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.creatorReferrals}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CreatorReferralsPage
                small={small}
                size={size}
                contentIdEnabled={flags?.contentIdEnabled}
                referralBonusPercent={flags?.referralBonusPercent}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productAssetDetails}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductAssetDetailsPage
                small={small}
                size={size}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productAssetUpload}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductAssetUploadPage
                small={small}
                size={size}
                featureConfig={{
                  feature: flags?.contentIdConfig,
                  contentIdAssetUploadEnabled: flags?.contentIdAssetUploadEnabled,
                  hasAllowlistedChannels: flags?.hasAllowlistedChannels,
                }}
                companyId={companyId}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productAssetUploadDetails}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductAssetUploadDetailsPage
                small={small}
                size={size}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productClaimsList}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductClaimsListPage
                small={small}
                size={size}
                featureConfig={flags?.contentIdConfig}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productClaimDetails}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ProductClaimDetailsPage
                small={small}
                size={size}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productManualClaimSubmit}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ManualClaimSubmitPage
                small={small}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.productManualClaimRequest}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ManualClaimRequestDetailPage
                small={small}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.account}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <AccountPage
                small={small}
                size={size}
                flags={flags}
                unconfiguredCompany={flags?.unconfiguredCompany}
                paymentPending={paymentPending}
                paymentLink={paymentLink}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.payoutSetup}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <PayoutSetupPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.payoutStatus}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <PayoutStatusPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.manageSubscription}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <ManageSubscriptionPage
                small={small}
                paymentPending={paymentPending}
                paymentLink={paymentLink}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.cidCancellationRequest}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CidCancellationRequestPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.planUpgrade}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <PlanUpgradePage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.settings}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CompanySettingsPage
                small={small}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.teamsSettings}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <TeamsSettingsPage
                small={small}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.unitsSettings}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CompanyUnitsPage
                small={small}
                searchConfig={flags?.searchConfig}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.fingerprintingSettings}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <FingerprintingConfigPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.users}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CompanyUsersPage
                small={small}
                paymentPending={paymentPending}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path="*"
          element={(
            <Error404Page />
          )}
        />
      </Route>
      {/* Cyclops Auth Routes (requires isAuthenticated & valid token) */}
      <Route
        element={(
          <Navigation
            darkMode
            small={small}
            activePageType="product"
            companyMaintenanceSettings={companyMaintenanceSettings}
            {...authProps}
          />
        )}
      >
        <Route
          path={paths.cyclopsAnalytics}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CyclopsAnalyticsPage
                small={small}
                demoOnly={flags?.isCyclopsDemo}
                size={size}
                companyDemoData={cyclopsDemoData}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.cyclopsOrganizations}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CyclopsOrganizationsPage
                small={small}
                size={size}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.cyclopsEventsList}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CyclopsEventsListPage
                small={small}
                demoOnly={flags?.isCyclopsDemo}
                size={size}
                companyDemoData={cyclopsDemoData}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.cyclopsEventDetails}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CyclopsEventDetailsPage
                small={small}
                demoOnly={flags?.isCyclopsDemo}
                size={size}
                companyDemoData={cyclopsDemoData}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.cyclopsSettings}
          element={(
            <AuthRouteWrapper
              type="product"
              isAuthenticated={isAuthenticated}
              flags={flags}
            >
              <CyclopsSettingsPage
                small={small}
                demoOnly={flags?.isCyclopsDemo}
                size={size}
                companyDemoData={cyclopsDemoData}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path="*"
          element={(
            <Error404Page />
          )}
        />
      </Route>
      {/* Admin Auth Routes (requires isAdminAuthenticated & valid token) */}
      <Route
        element={(
          <Navigation
            small={small}
            activePageType="admin"
            {...authProps}
          />
        )}
      >
        <Route
          path={paths.adminAccount}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminAccountPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminSearches}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminSearchesPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminResults}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminResultsPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminCompliance}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminCompliancePage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminFileImportNew}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminNewFileImportPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminFileImportsHistory}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminImportsHistoryPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminCreateSearchesImport}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminImportSearchesPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminManualClaims}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminManualClaimsListPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminManualClaimRequest}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminManualClaimDetailPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminMonetizationRequests}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminMonetizationRequestsListPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminMonetizationRequestDetails}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminMonetizationRequestDetailsPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminCustomOnboarding}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminCustomOnboardingPage
                small={small}
                size={size}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminPayoutDashboard}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminPayoutDashboardPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminCidPayoutStatus}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminCidPayoutStatusPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminCidAnalytics}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminCidAnalyticsDashboardPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminYtAudits}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminYtAuditsPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminCustomOnboarding}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminCustomOnboardingPage
                small={small}
                size={size}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminOnboardings}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminOnboardingsListPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminOnboardingDetails}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminOnboardingDetailsPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminDemoLinks}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminDemoLinksPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminDemoSubmissions}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminDemoSubmissionsPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminNagraReviewCandidates}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminNagraReviewCandidatesPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminNagraManageOperations}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminNagraManageOperationsPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminNagraManualSubmission}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminNagraManualSubmissionPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
        <Route
          path={paths.adminNagraCandidateDetail}
          element={(
            <AuthRouteWrapper
              type="admin"
              isAdminAuthenticated={isAdminAuthenticated}
            >
              <AdminNagraCandidateDetailPage
                small={small}
                {...authProps}
              />
            </AuthRouteWrapper>
          )}
        />
      </Route>
    </Routes>
  );
};

function mapStateToProps(state) {
  return {
    isAuthenticated: state.auth.isAuthenticated,
    isDemo: state.auth.isDemo,
    companyFeatures: state.auth.companyFeatures,
    companyId: state.auth.companyId,
    companyName: state.auth.companyName,
    companyOnboardingId: state.auth.onboardingId,
    email: state.auth.email,
    firstName: state.auth.firstName,
    lastName: state.auth.lastName,
    paymentPending: state.auth.paymentPending,
    paymentLink: state.auth.paymentLink,
    stripeAccountSetupComplete: state.auth.stripeAccountSetupComplete,
    stripeAccountId: state.auth.stripeAccountId,
    isRedirectRequired: state.redirect.isRequired,
    redirectPath: state.redirect.path,
    redirectPush: state.redirect.push,
    faviconStatus: state.favicon.faviconStatus,
    companyMaintenanceSettings: state.companySettings.companyMaintenanceSettings,
    isAdminAuthenticated: state.adminAuth.isAdminAuthenticated,
    cyclopsConfig: state.appConfig.cyclopsConfig,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    clearRedirect: clearRedirectRequest,
    fetchCompanyMaintenanceSettings: initiateCompanyMaintenanceSettingsRequest,
    fetchOnboardingData: fetchCustomerOnboardingData,
  }, dispatch);
}

AppRoutes.propTypes = {
  size: PropTypes.string.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isDemo: PropTypes.bool,
  isAdminAuthenticated: PropTypes.bool.isRequired,
  isRedirectRequired: PropTypes.bool.isRequired,
  redirectPath: PropTypes.objectOf(PropTypes.any),
  redirectPush: PropTypes.bool.isRequired,
  faviconStatus: PropTypes.string.isRequired,
  clearRedirect: PropTypes.func.isRequired,
  companyFeatures: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    enabled: PropTypes.bool.isRequired,
    config: PropTypes.objectOf(PropTypes.any),
  })),
  fetchCompanyMaintenanceSettings: PropTypes.func.isRequired,
  fetchOnboardingData: PropTypes.func.isRequired,
  companyMaintenanceSettings: PropTypes.shape({
    showStatusMessage: PropTypes.bool.isRequired,
    statusMessage: PropTypes.string.isRequired,
    disabledPages: PropTypes.arrayOf(PropTypes.shape({
      disableReviewFeatures: PropTypes.bool.isRequired,
      disableSearchFeatures: PropTypes.bool.isRequired,
      disableResultsFeatures: PropTypes.bool.isRequired,
      expiresAt: PropTypes.string.isRequired,
    })),
  }),
  companyId: PropTypes.number,
  companyName: PropTypes.string,
  companyOnboardingId: PropTypes.string,
  email: PropTypes.string,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  paymentPending: PropTypes.bool,
  paymentLink: PropTypes.string,
  stripeAccountSetupComplete: PropTypes.bool,
  stripeAccountId: PropTypes.string,
  cyclopsConfig: PropTypes.arrayOf(PropTypes.any),
};

export default connect(mapStateToProps, mapDispatchToProps)(AppRoutes);
